import { useState, useRef, useEffect, useCallback } from "react";
import { Send, BarChart3, Calculator, Map, Plus, CheckCircle2, FileText } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Navbar } from "../components/Navbar";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { logMessageSent, logPropertySearch, logPageView } from "../utils/analytics";
import { saveChat, updateChat, loadChat, getClientPurchaseType } from "../supabase";

const quickActions = [
  { icon: BarChart3, label: "Analizar anuncio", prompt: "Quiero analizar un anuncio inmobiliario" },
  { icon: Calculator, label: "Calcular hipoteca", prompt: "Ayúdame a calcular mi hipoteca" },
  { icon: Map, label: "Viabilidad financiera", prompt: "ver mi preparación para la compra de vivienda" },
];

/**
 * Verifica si un JSON es de propiedades (para la API) y no debe formatearse
 */
const isPropertySearchJSON = (data) => {
  // Detectar JSON de búsqueda de propiedades (para Apify)
  const propertySearchKeys = [
    'apifyReady', 'searchReady', 'userConfirmed',
    'bathrooms', 'bedrooms', 'country', 'location',
    'maxItems', 'maxPrice', 'minPrice', 'minSize',
    'operation', 'propertyType', 'proxyConfiguration'
  ];
  
  // Si tiene estas claves, es un JSON de propiedades
  return propertySearchKeys.some(key => key in data);
};

/**
 * Formatea un mensaje que puede contener JSON, HTML o texto estructurado
 */
const formatMessage = (text) => {
  if (!text) return '';
  
  // Limpiar y preparar el texto
  let cleanText = text.trim();
  
  // Intentar detectar y parsear JSON
  try {
    // Buscar JSON en el texto (puede estar al inicio, al final, o en medio)
    const jsonMatch = cleanText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const jsonStr = jsonMatch[0];
      const parsed = JSON.parse(jsonStr);
      
      // NO formatear si es un JSON de propiedades (para la API)
      if (isPropertySearchJSON(parsed)) {
        // Devolver el texto original sin formatear para que funcione la API
        return formatPlainText(cleanText);
      }
      
      // Si es un objeto con estructura conocida, formatearlo bonito
      if (parsed.requisitos || parsed.pasos) {
        return formatStructuredData(parsed);
      }
      
      // Si tiene zonas_baratas u otras estructuras conocidas
      if (parsed.zonas_baratas || parsed.zonas || parsed.areas) {
        return formatZonesData(parsed);
      }
      
      // Si es otro tipo de JSON, formatearlo genéricamente de manera bonita
      return formatGenericJSON(parsed, cleanText);
    }
  } catch (e) {
    // Si no es JSON válido, continuar con el texto original
  }
  
  // Manejar HTML (etiquetas <p>, <br>, etc.)
  if (cleanText.includes('<p>') || cleanText.includes('<br') || cleanText.includes('<div')) {
    return formatHTML(cleanText);
  }
  
  // Si no hay JSON ni HTML, formatear texto normal con párrafos
  return formatPlainText(cleanText);
};

/**
 * Formatea texto HTML de forma segura
 */
const formatHTML = (html) => {
  // Extraer texto de las etiquetas HTML de forma segura
  // Remover etiquetas HTML pero preservar el contenido
  let text = html
    .replace(/<p[^>]*>/gi, '\n\n')  // Reemplazar <p> con doble salto de línea
    .replace(/<\/p>/gi, '')          // Remover </p>
    .replace(/<br\s*\/?>/gi, '\n')  // Reemplazar <br> con salto de línea
    .replace(/<div[^>]*>/gi, '\n\n') // Reemplazar <div> con doble salto de línea
    .replace(/<\/div>/gi, '')        // Remover </div>
    .replace(/<[^>]+>/g, '')         // Remover cualquier otra etiqueta HTML
    .trim();
  
  // Formatear el texto resultante
  return formatPlainText(text);
};

/**
 * Formatea texto plano con párrafos bien estructurados
 */
const formatPlainText = (text) => {
  // Dividir por dobles saltos de línea o puntos seguidos de espacio y mayúscula
  const paragraphs = text
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);
  
  if (paragraphs.length === 1) {
    // Si solo hay un párrafo, dividirlo por oraciones largas
    return formatPlainTextContent(text);
  }
  
  return (
    <div className="formatted-text">
      {paragraphs.map((paragraph, index) => (
        <p key={index} className="mb-3 leading-relaxed last:mb-0">
          {formatPlainTextContent(paragraph)}
        </p>
      ))}
    </div>
  );
};

/**
 * Formatea el contenido de un párrafo, dividiendo oraciones largas
 */
const formatPlainTextContent = (text) => {
  // Dividir por oraciones (punto seguido de espacio y mayúscula, o punto final)
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
  
  if (sentences.length <= 1) {
    // Si es una sola oración o texto corto, devolverlo tal cual
    return text.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < text.split('\n').length - 1 && <br />}
      </span>
    ));
  }
  
  // Si hay múltiples oraciones, mostrarlas con espaciado
  return sentences.map((sentence, index) => (
    <span key={index}>
      {sentence.trim()}
      {index < sentences.length - 1 && ' '}
    </span>
  ));
};

/**
 * Formatea datos estructurados (requisitos, pasos, etc.)
 */
const formatStructuredData = (data) => {
  const parts = [];
  
  if (data.requisitos && Array.isArray(data.requisitos)) {
    parts.push(
      <div key="requisitos" className="mb-4 pb-4 border-b border-primary/10">
        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
          Requisitos
        </h4>
        <ul className="space-y-2.5">
          {data.requisitos.map((item, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <span className="text-foreground text-sm leading-relaxed">{item.requisito || item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  
  if (data.pasos && Array.isArray(data.pasos)) {
    parts.push(
      <div key="pasos" className="mb-4">
        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-base">
          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
          Pasos
        </h4>
        <ol className="space-y-2.5 ml-1">
          {data.pasos.map((item, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <span className="text-primary font-semibold text-sm flex-shrink-0 min-w-[1.5rem]">{index + 1}.</span>
              <span className="text-foreground text-sm leading-relaxed">{item.paso || item}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  
  // Si hay otros campos, mostrarlos también
  Object.keys(data).forEach((key) => {
    if (key !== 'requisitos' && key !== 'pasos') {
      const value = data[key];
      if (Array.isArray(value) && value.length > 0) {
        parts.push(
          <div key={key} className="mb-4 pb-4 border-b border-primary/10 last:border-0">
            <h4 className="font-semibold text-foreground mb-3 capitalize text-base">{key.replace(/_/g, ' ')}</h4>
            <ul className="space-y-2.5">
              {value.map((item, index) => (
                <li key={index} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground text-sm leading-relaxed">
                    {typeof item === 'object' ? Object.values(item)[0] : item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      } else if (typeof value === 'string' && value.trim()) {
        parts.push(
          <div key={key} className="mb-4">
            <h4 className="font-semibold text-foreground mb-2 capitalize text-base">{key.replace(/_/g, ' ')}</h4>
            <p className="text-foreground text-sm leading-relaxed">{value}</p>
          </div>
        );
      }
    }
  });
  
  return parts.length > 0 ? <div className="structured-content">{parts}</div> : null;
};

/**
 * Formatea datos de zonas (zonas_baratas, etc.)
 */
const formatZonesData = (data) => {
  const parts = [];
  
  // Formatear zonas_baratas
  if (data.zonas_baratas && Array.isArray(data.zonas_baratas)) {
    parts.push(
      <div key="zonas" className="space-y-3">
        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2 text-base">
          <Map className="w-4 h-4 text-primary flex-shrink-0" />
          Zonas más económicas
        </h4>
        <div className="grid gap-3">
          {data.zonas_baratas.map((zona, index) => (
            <div key={index} className="bg-secondary/50 rounded-lg p-3 border border-primary/10">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h5 className="font-semibold text-foreground text-sm mb-1">
                    {zona.nombre || zona.name || `Zona ${index + 1}`}
                  </h5>
                  {zona.precio_m2 && (
                    <p className="text-muted-foreground text-xs">
                      Precio: <span className="text-primary font-medium">{zona.precio_m2}</span>
                    </p>
                  )}
                  {zona.precio && (
                    <p className="text-muted-foreground text-xs">
                      Precio: <span className="text-primary font-medium">{zona.precio}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Formatear otras estructuras similares
  Object.keys(data).forEach((key) => {
    if (key !== 'zonas_baratas' && Array.isArray(data[key]) && data[key].length > 0) {
      const items = data[key];
      if (items[0] && typeof items[0] === 'object') {
        parts.push(
          <div key={key} className="mt-4 space-y-3">
            <h4 className="font-semibold text-foreground mb-3 capitalize text-base">
              {key.replace(/_/g, ' ')}
            </h4>
            <div className="grid gap-3">
              {items.map((item, index) => (
                <div key={index} className="bg-secondary/50 rounded-lg p-3 border border-primary/10">
                  {Object.entries(item).map(([itemKey, itemValue]) => (
                    <div key={itemKey} className="mb-1 last:mb-0">
                      <span className="text-foreground text-sm">
                        <span className="font-medium capitalize">{itemKey.replace(/_/g, ' ')}:</span>{' '}
                        <span className="text-primary">{String(itemValue)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
  });
  
  return parts.length > 0 ? <div className="formatted-zones">{parts}</div> : null;
};

/**
 * Formatea JSON genérico de manera legible y bonita
 */
const formatGenericJSON = (data, originalText) => {
  // Extraer el texto antes y después del JSON
  const jsonMatch = originalText.match(/\{[\s\S]*\}/);
  const beforeJSON = jsonMatch ? originalText.substring(0, jsonMatch.index).trim() : '';
  const afterJSON = jsonMatch ? originalText.substring(jsonMatch.index + jsonMatch[0].length).trim() : '';
  
  const parts = [];
  
  if (beforeJSON) {
    parts.push(
      <div key="before" className="mb-4">
        {formatPlainText(beforeJSON)}
      </div>
    );
  }
  
  // Formatear el JSON de manera bonita
  const formatJSONValue = (value, depth = 0) => {
    if (Array.isArray(value)) {
      return (
        <div className="ml-4 space-y-2">
          {value.map((item, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <div className="flex-1">
                {typeof item === 'object' ? formatJSONValue(item, depth + 1) : <span className="text-foreground">{String(item)}</span>}
              </div>
            </div>
          ))}
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="ml-4 space-y-2">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="flex items-start gap-2">
              <span className="text-primary font-medium capitalize min-w-[100px]">{k.replace(/_/g, ' ')}:</span>
              <div className="flex-1">
                {typeof v === 'object' ? formatJSONValue(v, depth + 1) : <span className="text-foreground">{String(v)}</span>}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <span className="text-foreground">{String(value)}</span>;
  };
  
  parts.push(
    <div key="json" className="bg-secondary/50 rounded-lg p-4 mb-4 border border-primary/10">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="mb-3 last:mb-0">
          <div className="font-semibold text-foreground mb-2 capitalize text-sm">
            {key.replace(/_/g, ' ')}
          </div>
          <div className="ml-2">
            {formatJSONValue(value)}
          </div>
        </div>
      ))}
    </div>
  );
  
  if (afterJSON) {
    parts.push(
      <div key="after" className="mt-4">
        {formatPlainText(afterJSON)}
      </div>
    );
  }
  
  return parts.length > 0 ? <div className="formatted-json">{parts}</div> : formatPlainText(originalText);
};

const Chat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [savedChatId, setSavedChatId] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedMessageCount, setLastSavedMessageCount] = useState(0);
  const [lastSavedSessionId, setLastSavedSessionId] = useState(null);
  const [purchaseType, setPurchaseType] = useState(null); // 'solo' o 'pareja'
  const saveTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Generar un sessionId único para cada sesión de chat
  const generateSessionId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };
  
  const [sessionId, setSessionId] = useState(generateSessionId());

  // Función para generar nombre automático del chat
  const generateChatName = (messages) => {
    // Buscar el primer mensaje del usuario
    const firstUserMessage = messages.find(msg => !msg.isBot);
    if (firstUserMessage && firstUserMessage.text) {
      const words = firstUserMessage.text.trim().split(' ').slice(0, 3);
      return words.join(' ') + (firstUserMessage.text.split(' ').length > 3 ? '...' : '');
    }
    return 'Nueva conversación';
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Enfocar automáticamente el input cuando el bot termine de escribir
  useEffect(() => {
    if (!isTyping && inputRef.current) {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [isTyping]);

  // Función para cargar el último chat activo (optimizada)
  const loadLastActiveChat = useCallback(async (chatId) => {
    try {
      const chat = await loadChat(user.uid, chatId);
      if (chat) {
        setMessages(chat.messages || []);
        setSavedChatId(chat.id);
        if (chat.session_id) {
          setSessionId(chat.session_id);
        }
      } else {
        // Si no se puede cargar el chat, mostrar bienvenida
        const welcomeMessage = {
          id: 0,
          text: `Qué tal, ${user?.displayName} ¿Pensando en comprar tu casa… o solo me quieres poner a prueba?
Soy GABI, tu asistente, y estoy aquí para ayudarte en todo el proceso.
¿Por donde quieres empezar?`,
          isBot: true,
          timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages([welcomeMessage]);
        // Limpiar el localStorage si el chat no existe
        localStorage.removeItem('lastActiveChat');
      }
    } catch (error) {
      // En caso de error, mostrar bienvenida
      const welcomeMessage = {
        id: 0,
        text: `Qué tal, ${user?.displayName} ¿Pensando en comprar tu casa… o solo me quieres poner a prueba?
Soy GABI, tu asistente, y estoy aquí para ayudarte en todo el proceso.
¿Por donde quieres empezar?`,
        isBot: true,
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([welcomeMessage]);
      // Limpiar el localStorage si hay error
      localStorage.removeItem('lastActiveChat');
    }
  }, [user?.uid, user?.displayName]);

  // Guardar propiedades de mensajes en sessionStorage automáticamente
  useEffect(() => {
    // Cuando se cargan o actualizan mensajes, guardar las propiedades de cada mensaje
    // que tenga propiedades, para que estén disponibles cuando se haga clic en los botones
    messages.forEach(message => {
      if (message.showPropertiesButton && message.properties && message.id) {
        const storageKey = `chatProperties_${message.id}`;
        const existingData = sessionStorage.getItem(storageKey);
        
        // Solo guardar si no existe o si las propiedades han cambiado
        if (!existingData || JSON.stringify(message.properties) !== JSON.stringify(JSON.parse(existingData)?.properties)) {
          sessionStorage.setItem(storageKey, JSON.stringify({
            properties: message.properties,
            propertiesCount: message.propertiesCount || message.properties.length,
            messageId: message.id
          }));
        }
      }
    });
  }, [messages]);

  // Cargar purchase_type del usuario al montar el componente
  useEffect(() => {
    const loadPurchaseType = async () => {
      if (user?.email) {
        try {
          const type = await getClientPurchaseType(user.email);
          setPurchaseType(type);
        } catch (error) {
        }
      }
    };
    loadPurchaseType();
  }, [user]);

  // Mensaje de bienvenida inicial
  useEffect(() => {
    // Verificar si hay un chat cargado desde el dashboard
    if (location.state?.loadedChat) {
      const loadedChat = location.state.loadedChat;
      setMessages(loadedChat.messages || []);
      setSavedChatId(loadedChat.id); // Marcar como chat guardado
      
      // RESTAURAR el sessionId original del chat cargado
      if (loadedChat.session_id) {
        setSessionId(loadedChat.session_id);
      }
      
      // Guardar este chat como último chat activo
      localStorage.setItem('lastActiveChat', JSON.stringify({
        id: loadedChat.id,
        sessionId: loadedChat.session_id,
        timestamp: Date.now()
      }));
    } else {
      // Verificar si hay un último chat activo guardado (optimizado)
      const lastActiveChat = localStorage.getItem('lastActiveChat');
      
      if (lastActiveChat && user) {
        try {
          const chatData = JSON.parse(lastActiveChat);
          const timeDiff = Date.now() - chatData.timestamp;
          const maxAge = 2 * 60 * 60 * 1000; // Reducido a 2 horas para ser más rápido
          
          // Solo restaurar si no ha pasado mucho tiempo
          if (timeDiff < maxAge && chatData.id) {
            // Cargar directamente el chat sin verificaciones adicionales
            loadLastActiveChat(chatData.id);
            return;
          } else {
            localStorage.removeItem('lastActiveChat');
          }
        } catch (error) {
          localStorage.removeItem('lastActiveChat');
        }
      }
      
      // Si no hay chat para restaurar, mostrar mensaje de bienvenida
      const welcomeMessage = {
        id: 0,
        text: `Qué tal, ${user?.displayName} ¿Pensando en comprar tu casa… o solo me quieres poner a prueba?
Soy GABI, tu asistente, y estoy aquí para ayudarte en todo el proceso.
¿Por donde quieres empezar?`,
        isBot: true,
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages([welcomeMessage]);
    }
    
    // Registrar vista de página
    logPageView('chat');
  }, [user, location.state, sessionId, loadLastActiveChat]);


  // Función para formatear las respuestas del chatbot
  const formatBotResponse = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    let formattedText = text;
    
    // Convertir texto en negrita (**texto**)
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convertir texto en cursiva (*texto*)
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Dividir en líneas para procesar
    const lines = formattedText.split('\n');
    const processedLines = [];
    let inList = false;
    let listItems = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Detectar títulos (líneas que terminan con :)
      if (line.match(/^(.+):$/)) {
        // Cerrar lista anterior si existe
        if (inList && listItems.length > 0) {
          processedLines.push(`<ul>${listItems.join('')}</ul>`);
          listItems = [];
          inList = false;
        }
        processedLines.push(`<h3>${line}</h3>`);
        continue;
      }
      
      // Detectar elementos de lista
      if (line.match(/^[-•]\s+(.+)$/) || line.match(/^\d+\.\s+(.+)$/)) {
        if (!inList) {
          inList = true;
        }
        const content = line.replace(/^[-•]\s+/, '').replace(/^\d+\.\s+/, '');
        listItems.push(`<li>${content}</li>`);
        continue;
      }
      
      // Si no es elemento de lista y estábamos en una lista, cerrar la lista
      if (inList && listItems.length > 0) {
        processedLines.push(`<ul>${listItems.join('')}</ul>`);
        listItems = [];
        inList = false;
      }
      
      // Líneas normales
      if (line) {
        processedLines.push(`<p>${line}</p>`);
      } else {
        // Línea vacía
        processedLines.push('<br>');
      }
    }
    
    // Cerrar lista si quedó abierta
    if (inList && listItems.length > 0) {
      processedLines.push(`<ul>${listItems.join('')}</ul>`);
    }
    
    return processedLines.join('');
  };

  // Función para analizar y procesar JSONs del chatbot
  const processChatbotResponse = (response) => {
    // Verificar si la respuesta es un array (nuevo formato)
    if (Array.isArray(response)) {
      // Buscar objetos con propiedad 'output' que contengan JSON
      for (const item of response) {
        if (item && item.output && typeof item.output === 'string') {
          try {
            // Parsear el JSON dentro de la propiedad output
            const jsonResponse = JSON.parse(item.output);
            
            // Verificar si es un JSON con apifyReady
            if (jsonResponse.apifyReady === true) {
              // Extraer los datos para la búsqueda
              const searchData = {
                bathrooms: jsonResponse.bathrooms,
                bedrooms: jsonResponse.bedrooms,
                country: jsonResponse.country,
                location: jsonResponse.location,
                maxItems: jsonResponse.maxItems,
                maxPrice: jsonResponse.maxPrice,
                minPrice: jsonResponse.minPrice,
                minSize: jsonResponse.minSize,
                operation: jsonResponse.operation,
                propertyType: jsonResponse.propertyType,
                proxyConfiguration: jsonResponse.proxyConfiguration
              };
              
              // Retornar un objeto indicando que es un JSON de Apify
              return {
                isApifyRequest: true,
                searchData: searchData,
                originalResponse: response
              };
            }
          } catch (error) {
            // Error al parsear JSON
          }
        }
      }
    }
    
    // Verificar si la respuesta ya es un objeto (formato directo)
    if (typeof response === 'object' && response !== null) {
      // Si la respuesta ya es un objeto
      if (response.apifyReady === true) {
        // Extraer los datos para la búsqueda
        const searchData = {
          bathrooms: response.bathrooms,
          bedrooms: response.bedrooms,
          country: response.country,
          location: response.location,
          maxItems: response.maxItems,
          maxPrice: response.maxPrice,
          minPrice: response.minPrice,
          minSize: response.minSize,
          operation: response.operation,
          propertyType: response.propertyType,
          proxyConfiguration: response.proxyConfiguration
        };
        
        // Retornar un objeto indicando que es un JSON de Apify
        return {
          isApifyRequest: true,
          searchData: searchData,
          originalResponse: response
        };
      }
    }
    
    // Verificar si la respuesta es un string que contiene JSON (formato anterior)
    if (typeof response === 'string') {
      // Primero intentar parsear el string completo como JSON
      try {
        const jsonResponse = JSON.parse(response);
        if (jsonResponse.apifyReady === true) {
          // Extraer los datos para la búsqueda
          const searchData = {
            bathrooms: jsonResponse.bathrooms,
            bedrooms: jsonResponse.bedrooms,
            country: jsonResponse.country,
            location: jsonResponse.location,
            maxItems: jsonResponse.maxItems,
            maxPrice: jsonResponse.maxPrice,
            minPrice: jsonResponse.minPrice,
            minSize: jsonResponse.minSize,
            operation: jsonResponse.operation,
            propertyType: jsonResponse.propertyType,
            proxyConfiguration: jsonResponse.proxyConfiguration
          };
          
          // Retornar un objeto indicando que es un JSON de Apify
          return {
            isApifyRequest: true,
            searchData: searchData,
            originalResponse: response
          };
        }
      } catch (error) {
        // String no es un JSON válido completo, buscar JSON parcial
      }
      
      // Buscar JSON en la respuesta, incluso si hay texto antes
      let jsonMatch = null;
      
      // Buscar JSON dentro de bloques markdown
      if (response.includes('```json')) {
        const jsonBlockMatch = response.match(/```json\s*(\{[\s\S]*?\})\s*```/);
        if (jsonBlockMatch) {
          jsonMatch = jsonBlockMatch[1];
        }
      }
      
      // Si no se encontró en markdown, buscar cualquier objeto JSON en la respuesta
      if (!jsonMatch) {
        const jsonObjectMatch = response.match(/\{[\s\S]*?\}/);
        if (jsonObjectMatch) {
          jsonMatch = jsonObjectMatch[0];
        }
      }
      
      if (jsonMatch) {
        try {
          // Parsear el JSON encontrado
          const jsonResponse = JSON.parse(jsonMatch);
          
          // Verificar si es un JSON con apifyReady
          if (jsonResponse.apifyReady === true) {
            // Extraer los datos para la búsqueda
            const searchData = {
              bathrooms: jsonResponse.bathrooms,
              bedrooms: jsonResponse.bedrooms,
              country: jsonResponse.country,
              location: jsonResponse.location,
              maxItems: jsonResponse.maxItems,
              maxPrice: jsonResponse.maxPrice,
              minPrice: jsonResponse.minPrice,
              minSize: jsonResponse.minSize,
              operation: jsonResponse.operation,
              propertyType: jsonResponse.propertyType,
              proxyConfiguration: jsonResponse.proxyConfiguration
            };
            
            // Retornar un objeto indicando que es un JSON de Apify
            return {
              isApifyRequest: true,
              searchData: searchData,
              originalResponse: response
            };
          }
        } catch (error) {
          // Error al parsear JSON encontrado
        }
      }
    }
    
    // Si no es un JSON de Apify, retornar la respuesta normal
    return {
      isApifyRequest: false,
      response: response
    };
  };

  // Función para llamar directamente a la API de Apify
  const searchProperties = async (searchData) => {
    try {
      // Validar que existe la URL y la key de Apify
      const apifyUrl = process.env.REACT_APP_APIFY_API_URL;
      const apifyKey = process.env.REACT_APP_APIFY_API_KEY;
      
      if (!apifyUrl) {
        throw new Error('REACT_APP_APIFY_API_URL no está configurada en tu .env');
      }
      
      if (!apifyKey) {
        throw new Error('REACT_APP_APIFY_API_KEY no está configurada en tu .env');
      }

      // Formatear bathrooms y bedrooms como arrays de strings
      const formatArrayField = (value) => {
        if (!value) return undefined;
        if (Array.isArray(value)) {
          return value.map(v => String(v));
        }
        return [String(value)];
      };

      const requestData = {
        apifyReady: true,
        searchReady: true,
        userConfirmed: true,
        bathrooms: formatArrayField(searchData.bathrooms),
        bedrooms: formatArrayField(searchData.bedrooms),
        country: searchData.country || "es",
        location: searchData.location,
        maxItems: searchData.maxItems || 50,
        maxPrice: searchData.maxPrice != null ? String(searchData.maxPrice) : undefined,
        minPrice: searchData.minPrice != null ? String(searchData.minPrice) : undefined,
        minSize: searchData.minSize != null ? String(searchData.minSize) : undefined,
        operation: searchData.operation || "sale",
        propertyType: searchData.propertyType || "homes",
        proxyConfiguration: {
          useApifyProxy: true,
          apifyProxyGroups: ["RESIDENTIAL"]
        }
      };

      // Construir la URL completa con el token
      const apiUrl = apifyUrl.includes('?token=') 
        ? apifyUrl.replace(/token=[^&]*/, `token=${apifyKey}`)
        : `${apifyUrl}?token=${apifyKey}`;

      console.log('🔍 [DEBUG] Enviando JSON a Apify:', apiUrl.replace(/token=[^&]*/, 'token=HIDDEN'));
      console.log('🔍 [DEBUG] JSON que se envía:', JSON.stringify(requestData, null, 2));

      // Llamar directamente a la API de Apify
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      console.log('🔍 [DEBUG] Response status:', response.status, response.statusText);
      console.log('🔍 [DEBUG] Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [ERROR] Response error:', errorText);
        throw new Error(`Error en la respuesta de Apify: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ [DEBUG] Response data recibido:', data);
      
      // Si el endpoint devuelve un run, necesitamos obtener el dataset
      if (data.data && data.data.defaultDatasetId) {
        const datasetId = data.data.defaultDatasetId;
        console.log('🔍 [DEBUG] Obteniendo datos del dataset:', datasetId);
        
        // Esperar un poco y obtener los datos del dataset
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const datasetUrl = `https://api.apify.com/v2/datasets/${datasetId}/items?token=${apifyKey}&format=json`;
        const datasetResponse = await fetch(datasetUrl);
        
        if (datasetResponse.ok) {
          const properties = await datasetResponse.json();
          console.log('✅ [DEBUG] Propiedades obtenidas:', properties?.length || 0);
          return properties;
        }
      }
      
      // Si ya viene como array, devolverlo directamente
      if (Array.isArray(data)) {
        console.log('✅ [DEBUG] Propiedades recibidas directamente:', data.length);
      return data;
      }
      
      // Si viene en data.data
      if (data.data && Array.isArray(data.data)) {
        console.log('✅ [DEBUG] Propiedades en data.data:', data.data.length);
        return data.data;
      }
      
      console.log('⚠️ [WARNING] Formato de respuesta no reconocido:', data);
      return [];
    } catch (error) {
      console.error('❌ [ERROR] Error completo:', error);
      throw error;
    }
  };

  const sendMessageToAPI = async (message) => {
    try {
      const payload = {
        sessionId: sessionId,
        action: "sendMessage",
        chatInput: message,
        userEmail: user?.email || null,
        userName: user?.displayName?.trim() || null
      };

      // Seleccionar la URL del chatbot según el tipo de compra
      const chatbotUrl = purchaseType === 'pareja' 
        ? process.env.REACT_APP_N8N_WEBHOOK_CHAT_PAREJA
        : process.env.REACT_APP_N8N_WEBHOOK_CHAT_SOLO;

      // Validar que el webhook esté configurado
      if (!chatbotUrl) {
        throw new Error('Webhook de chat no configurado. Verifica REACT_APP_N8N_WEBHOOK_CHAT_SOLO o REACT_APP_N8N_WEBHOOK_CHAT_PAREJA');
      }

      const response = await fetch(chatbotUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      const data = await response.json();
      
      // Retornar exactamente lo que viene del chatbot
      const botResponse = data.output || data.response || data.message || data.text || data;
      
      return botResponse;
    } catch (error) {
      throw error;
    }
  };

  const handleSend = async (messageText) => {
    const text = messageText || input.trim();
    if (!text) return;

    const userMessage = {
      id: Date.now(),
      text: text,
      isBot: false,
      timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => {
      const updatedMessages = [...prev, userMessage];
      return updatedMessages;
    });
    setInput('');
    setIsTyping(true);
    
    // Registrar envío de mensaje
    logMessageSent();

    try {
      const botResponse = await sendMessageToAPI(text);
      
      // Procesar la respuesta del chatbot
      const processedResponse = processChatbotResponse(botResponse);
      
      if (processedResponse.isApifyRequest) {
        // Es un JSON de Apify, no mostrar al usuario, hacer la búsqueda directamente
        
        // Mostrar mensaje de búsqueda al usuario
        const searchMessage = {
          id: Date.now() + 1,
          text: "🔍 Buscando propiedades según tus criterios...",
          isBot: true,
          timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
        };
        setMessages(prev => [...prev, searchMessage]);
        
        try {
          // Registrar búsqueda de propiedades
          logPropertySearch(
            processedResponse.searchData.location || 'N/A',
            processedResponse.searchData.propertyType || 'N/A'
          );
          
          // Realizar búsqueda de propiedades con los datos del JSON
          const propertiesData = await searchProperties(processedResponse.searchData);
          
          if (propertiesData && propertiesData.length > 0) {
            // Ordenar las propiedades por precio (de menor a mayor)
            const sortedProperties = propertiesData.sort((a, b) => {
              const priceA = a.priceInfo?.price?.amount || 0;
              const priceB = b.priceInfo?.price?.amount || 0;
              return priceA - priceB;
            });
            
            const successMessage = {
              id: Date.now() + 2,
              text: `✅ ¡Perfecto! He encontrado ${propertiesData.length} propiedades que se ajustan a tus criterios.`,
              isBot: true,
              timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
              hasProperties: true,
              propertiesCount: propertiesData.length,
              properties: sortedProperties, // Guardar las propiedades en el mensaje
              showPropertiesButton: true // Indicador para mostrar el botón
            };
            setMessages(prev => {
              const updatedMessages = [...prev, successMessage];
              // Guardar inmediatamente para no perder el botón si el usuario navega rápido
              autoSaveChat(updatedMessages);
              return updatedMessages;
            });
          } else {
            const noResultsMessage = {
              id: Date.now() + 2,
              text: "❌ No se encontraron propiedades con los criterios especificados. ¿Podrías ajustar tus preferencias?",
              isBot: true,
              timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => {
              const updatedMessages = [...prev, noResultsMessage];
              return updatedMessages;
            });
          }
        } catch (error) {
          const errorMessage = {
            id: Date.now() + 2,
            text: "❌ Error al buscar propiedades. Inténtalo de nuevo más tarde.",
            isBot: true,
            timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      } else {
        // Respuesta normal del chatbot, mostrar al usuario
        const formattedResponse = formatBotResponse(processedResponse.response);
        const botMessage = {
          id: Date.now() + 1,
          text: formattedResponse,
          isBot: true,
          timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
        };

        setMessages(prev => {
          const updatedMessages = [...prev, botMessage];
          // Guardar automáticamente SOLO al final de la respuesta del bot
          debouncedAutoSave(updatedMessages);
          return updatedMessages;
        });
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Error de conexión con el chatbot. Inténtalo de nuevo.",
        isBot: true,
        timestamp: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => {
        const updatedMessages = [...prev, errorMessage];
        return updatedMessages;
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickAction = (prompt) => {
    handleSend(prompt);
  };

  const handleNewChat = () => {
    // Limpiar el chat actual
    setMessages([]);
    setSavedChatId(null);
    setIsAutoSaving(false);
    setLastSavedMessageCount(0);
    setLastSavedSessionId(null);
    
    // Limpiar último chat activo del localStorage
    localStorage.removeItem('lastActiveChat');
    
    // Generar nuevo sessionId
    const newSessionId = generateSessionId();
    window.location.href = `/chat?session=${newSessionId}`;
  };


  // Función para guardar automáticamente con debounce
  const debouncedAutoSave = (updatedMessages) => {
    // Cancelar el timeout anterior si existe
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Crear nuevo timeout
    saveTimeoutRef.current = setTimeout(() => {
      autoSaveChat(updatedMessages);
    }, 3000); // 3 segundos de delay para evitar duplicados
  };

  // Función para guardar automáticamente
  const autoSaveChat = async (updatedMessages) => {
    if (!user || isAutoSaving) return;
    
    // Verificar si ya se guardó para este sessionId y número de mensajes
    if (lastSavedSessionId === sessionId && updatedMessages.length <= lastSavedMessageCount) {
      return;
    }
    
    // Solo guardar si hay cambios en el número de mensajes
    if (updatedMessages.length <= lastSavedMessageCount) {
      return;
    }
    
    // Evitar guardar el mensaje de bienvenida inicial
    if (updatedMessages.length === 1 && updatedMessages[0].isBot) {
      return;
    }
    
    // Solo guardar si hay al menos un mensaje del usuario
    const userMessages = updatedMessages.filter(msg => !msg.isBot);
    if (userMessages.length === 0) {
      return;
    }
    
    try {
      setIsAutoSaving(true);
      
      if (savedChatId) {
        // SIEMPRE actualizar chat existente - NUNCA crear uno nuevo
        await updateChat(user.uid, savedChatId, updatedMessages);
        setLastSavedMessageCount(updatedMessages.length);
        setLastSavedSessionId(sessionId);
        
        // Guardar como último chat activo
        localStorage.setItem('lastActiveChat', JSON.stringify({
          id: savedChatId,
          sessionId: sessionId,
          timestamp: Date.now()
        }));
      } else {
        // SOLO crear nuevo chat si NO existe uno guardado
        const chatName = generateChatName(updatedMessages);
        const savedChat = await saveChat(user.uid, chatName, updatedMessages, sessionId);
        setSavedChatId(savedChat.id);
        setLastSavedMessageCount(updatedMessages.length);
        setLastSavedSessionId(sessionId);
        
        // Guardar como último chat activo
        localStorage.setItem('lastActiveChat', JSON.stringify({
          id: savedChat.id,
          sessionId: sessionId,
          timestamp: Date.now()
        }));
      }
    } catch (error) {
    } finally {
      setIsAutoSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />

      <main className="flex-1 container mx-auto px-4 pt-20 pb-6 flex flex-col max-w-5xl">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-6 mb-6">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.isBot ? "flex-row" : "flex-row-reverse"}`}
            >
              {/* Avatar */}
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                {message.isBot ? (
                  <img 
                    src="/gabi.png" 
                    alt="GABI" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  user?.photoURL ? (
                    <img 
                      src={`https://images.weserv.nl/?url=${encodeURIComponent(user.photoURL)}`}
                      alt="Usuario" 
                      className="w-full h-full object-cover rounded-full"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.parentElement?.querySelector('.avatar-fallback');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-secondary rounded-full flex items-center justify-center avatar-fallback" style={{ display: user?.photoURL ? 'none' : 'flex' }}>
                      <span className="text-lg">👤</span>
                    </div>
                  )
                )}
              </div>

              {/* Message bubble */}
              <div
                className={`max-w-[80%] rounded-3xl p-4 ${
                  message.isBot
                    ? "bg-secondary rounded-tl-none"
                    : "gradient-primary text-primary-foreground rounded-tr-none"
                }`}
              >
                <div className="whitespace-pre-line">
                  {formatMessage(message.text)}
                </div>
                
                {/* Botón para ver propiedades */}
                {message.showPropertiesButton && (
                  <div className="mt-3">
                    <Button
                      onClick={() => {
                        // Guardar propiedades en sessionStorage con el ID del mensaje como clave única
                        // Esto permite tener múltiples búsquedas, cada una con sus propias propiedades
                        const storageKey = `chatProperties_${message.id}`;
                        // Si no hay propiedades en el mensaje (p.ej. tras recargar o volver atrás),
                        // intentar recuperarlas de sessionStorage
                        let propertiesPayload = message.properties;
                        let propertiesCount = message.propertiesCount;
                        if (!propertiesPayload) {
                          const stored = sessionStorage.getItem(storageKey);
                          if (stored) {
                            try {
                              const parsed = JSON.parse(stored);
                              propertiesPayload = parsed.properties;
                              propertiesCount = parsed.propertiesCount;
                            } catch (e) {
                              // si falla el parseo, ignorar
                            }
                          }
                        }
                        if (propertiesPayload) {
                          sessionStorage.setItem(storageKey, JSON.stringify({
                            properties: propertiesPayload,
                            propertiesCount: propertiesCount,
                            messageId: message.id
                          }));
                        }
                        
                        // Navegar a la página de propiedades con los datos en el estado
                        navigate('/properties', { 
                          state: { 
                            properties: propertiesPayload || message.properties,
                            propertiesCount: propertiesCount || message.propertiesCount,
                            messageId: message.id // Pasar el ID del mensaje para recuperación
                          } 
                        });
                      }}
                      variant="hero"
                      className="w-full"
                    >
                      🏠 Ver {(message.propertiesCount) || (JSON.parse(sessionStorage.getItem(`chatProperties_${message.id}`) || '{}').propertiesCount) || 0} propiedades encontradas
                    </Button>
                  </div>
                )}
                
                <span
                  className={`text-xs mt-2 block ${
                    message.isBot ? "text-muted-foreground" : "text-primary-foreground/70"
                  }`}
                >
                  {message.timestamp}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-3">
              <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                <img 
                  src="/gabi.png" 
                  alt="GABI" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-secondary rounded-3xl rounded-tl-none p-4">
                <div className="flex gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.2s" }} />
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick actions */}
        {messages.length <= 2 && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-3">Acciones rápidas:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleQuickAction(action.prompt)}
                  className="glass px-4 py-2 rounded-full text-sm hover:border-primary/30 transition-all duration-300 flex items-center gap-2"
                >
                  <action.icon className="w-4 h-4 text-primary" />
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="glass rounded-3xl border border-primary/20 p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex gap-2"
          >
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              onClick={handleNewChat}
              title="Nuevo chat"
              className="flex-shrink-0"
            >
              <Plus className="w-5 h-5" />
            </Button>
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={isTyping}
            />
            <Button type="submit" variant="hero" size="icon" disabled={isTyping || !input.trim()}>
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>

      </main>
    </div>
  );
};

export default Chat;
